/**
 * ============================================================================
 * Accessible Accordion Component (Vanilla JS)
 * ============================================================================
 * Provides interactive accordion / collapsible sections with:
 * - Keyboard navigation (←, →, ↑, ↓, Home, End, Enter, Space)
 * - Mouse hover opening (optional)
 * - Lazy image loading via IntersectionObserver
 * - Smooth transition animation (CSS-driven via max-height)
 * - Optional picture container sync for visual transitions
 *
 * Works in two behavior modes:
 *   • "accordion"   — only one panel open at a time
 *   • "collapsible" — multiple panels can stay open simultaneously
 *
 * ----------------------------------------------------------------------------
 * 📄 DATA ATTRIBUTES (used in HTML)
 * ----------------------------------------------------------------------------
 *
 * 🔸 Container-level attributes (on main accordion wrapper):
 *
 *  data-lazy-init="accordion"
 *      Required. Marks element for JS initialization.
 *
 *  data-accordion-container
 *      Required. Identifies the accordion root element.
 *
 *  data-accordion-behavior="accordion" | "collapsible"
 *      Optional. Defines whether only one or multiple panels can be open.
 *      Default: "accordion"
 *
 *  data-open-on-hover="true"
 *      Optional. Enables hover-open mode for desktop.
 *
 *  data-hover-delay="300"
 *      Optional. Delay (ms) before closing panel on mouseleave (default 300ms).
 *
 *  data-accordion-controls="main-menu"
 *      Optional. Connects accordion to an image/picture container that
 *      updates when tabs change. The matching element should have
 *      `data-accordion-target="main-menu"`.
 *
 * ----------------------------------------------------------------------------
 * 🔹 Trigger-level attributes (on clickable elements inside header):
 *
 *  data-accordion="#panel-id"
 *      Required. CSS selector (ID) of the controlled panel.
 *
 *  data-accordion-initial="true"
 *      Optional. Marks this item to open automatically on page load.
 *
 *  data-image-path="path/to/image.jpg"
 *      Optional. If `data-accordion-controls` + picture container exist,
 *      defines the new image source to be loaded when this tab activates.
 *
 * ----------------------------------------------------------------------------
 * 🔹 Panel-level attributes (on collapsible content sections):
 *
 *  id="panel-id"
 *      Required. Unique panel identifier. Must match trigger’s data-accordion.
 *
 *  hidden
 *      Present when panel is closed (accessibility + semantics).
 *
 *  aria-hidden="true" / "false"
 *      Dynamically toggled by JS for screen readers.
 *
 * ----------------------------------------------------------------------------
 * 🔹 ARIA ATTRIBUTES (Accessibility)
 * ----------------------------------------------------------------------------
 *  Each trigger (button or div[role="button"]) should include:
 *      role="button"
 *      aria-expanded="true|false"   — updated automatically
 *      aria-controls="panel-id"     — links trigger to controlled panel
 *
 *  Each panel (content container) should include:
 *      role="region" (optional, recommended)
 *      aria-labelledby="trigger-id"
 *      hidden + aria-hidden         — managed by JS
 *
 * ----------------------------------------------------------------------------
 * 🔸 Example markup
 * ----------------------------------------------------------------------------
 *
 * <section class="accordion-header"
 *          data-lazy-init="accordion"
 *          data-accordion-container
 *          data-accordion-behavior="accordion"
 *          data-open-on-hover="true"
 *          data-hover-delay="300"
 *          data-accordion-controls="main-menu">
 *
 *     <div class="accordion-header__item">
 *         <div role="button"
 *              data-accordion="#panel-1"
 *              data-accordion-initial="true"
 *              aria-controls="panel-1"
 *              aria-expanded="false">
 *             <span>Industries</span>
 *         </div>
 *     </div>
 *
 *     ...
 * </section>
 *
 * <div id="panel-1" class="accordion-item" hidden aria-hidden="true">
 *     <div class="accordion-item__content"> ... </div>
 * </div>
 *
 * ----------------------------------------------------------------------------
 *  NOTES
 * ----------------------------------------------------------------------------
 * - Lazy-loaded images inside panels must use `data-src` for deferred loading.
 * - Picture sync uses `data-accordion-target` + `data-image-path` mechanism.
 * - CSS transition duration must match JS setting (default 550ms).
 *
 * ============================================================================
 */


/**
 * Utility function to find the closest ancestor with a specific class or data attribute.
 * @param {HTMLElement} element The starting element.
 * @param {string} selector The CSS selector for the ancestor.
 * @returns {HTMLElement|null} The found ancestor element, or null.
 */
function findAncestor(element, selector) {
	if (!element || !selector) return null;
	let current = element.parentElement;
	while (current) {
		if (current.matches(selector)) {
			return current;
		}
		current = current.parentElement;
	}
	return null;
}

/**
 * Manages an accordion/collapsible component with optional hover functionality and lazy image loading.
 */
export class Accordion {
	// Default settings for the accordion component.
	static DEFAULT_SETTINGS = {
		behavior: 'accordion', // 'accordion' (one open) or 'collapsible' (many open)
		openOnHover: false,    // Whether to open items on mouse hover
		hoverDelay: 300,       // Delay in milliseconds before closing on mouseleave (from entire item)
		transitionDuration: 550 // Must match CSS transition duration for maxHeight
	};

	/**
	 * Initializes a new Accordion instance.
	 * @param {HTMLElement} container The root HTML element of the accordion component.
	 * @param {Object} [options={}] Optional settings to override defaults.
	 */
	constructor(container, options = {}) {
		if (!(container instanceof HTMLElement)) {
			console.error('Accordion container must be an HTMLElement.', container);
			return;
		}
		this.container = container;
		this.settings = { ...Accordion.DEFAULT_SETTINGS, ...options };

		// Override settings with data attributes from the container
		if (this.container.dataset.accordionBehavior) {
			this.settings.behavior = this.container.dataset.accordionBehavior;
		}
		if (this.container.dataset.openOnHover === 'true') {
			this.settings.openOnHover = true;
		}
		if (this.container.dataset.hoverDelay) {
			this.settings.hoverDelay = parseInt(this.container.dataset.hoverDelay, 10);
		}

		this._pictureUpdateTimeout = null;
		this._closeHoverTimeout = null;
		this._isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

		this._triggers = Array.from(this.container.querySelectorAll('[data-accordion]'));
		this._panels = this._triggers.map(trigger => document.querySelector(trigger.dataset.accordion)).filter(Boolean);
		this._mainParent = findAncestor(this.container, '.content-section, .container');

		// Image update related elements
		const controlsId = this.container.dataset.accordionControls;
		this._pictureContainer = controlsId ? document.querySelector(`[data-accordion-target="${controlsId}"]`) : null;
		this._imageTarget = this._pictureContainer ? this._pictureContainer.querySelector('img') : null;

		// Intersection Observer for lazy loading images within accordion panels
		this._imageObserver = new IntersectionObserver(this._handleImageIntersection.bind(this), {
			rootMargin: '100px',
			threshold: 0
		});

		if (!this._triggers.length) {
			console.warn('Accordion component has no triggers.', this.container);
			return;
		}

		this._bindEvents();
		this._initialize();

		// Recalculate panel heights when resizing. iOS Safari fires `resize` on `window`
		// repeatedly while its dynamic toolbar collapses/expands during scroll (viewport
		// height only, width unchanged) — skip the reflow-heavy updateHeights() call in
		// that case, and rAF-throttle genuine width-driven resizes.
		this._lastResizeWidth = window.innerWidth;
		this._resizeRaf = null;
		this._resizeHandler = () => {
			const currentWidth = window.innerWidth;
			if (currentWidth === this._lastResizeWidth) return;
			this._lastResizeWidth = currentWidth;
			if (this._resizeRaf) return;
			this._resizeRaf = requestAnimationFrame(() => {
				this._resizeRaf = null;
				this.updateHeights();
			});
		};
		window.addEventListener('resize', this._resizeHandler, { passive: true });
	}

	/**
	 * Binds all necessary event listeners to triggers and panels.
	 * @private
	 */
	_bindEvents() {
		this._panels.forEach(panel => {
			panel.addEventListener('beforematch', this._handleBeforeMatch);
			panel.addEventListener('transitionend', this._handleTransitionEnd);
		});

		this._triggers.forEach(trigger => {
			trigger.addEventListener('click', this._handleClick);

			// Keyboard navigation support
			trigger.addEventListener('keydown', e => {
				const currentIndex = this._triggers.indexOf(trigger);
				switch (e.key) {
					case 'ArrowDown':
					case 'ArrowRight':
						e.preventDefault();
						this._focusNext(currentIndex);
						break;
					case 'ArrowUp':
					case 'ArrowLeft':
						e.preventDefault();
						this._focusPrev(currentIndex);
						break;
					case 'Home':
						e.preventDefault();
						this._triggers[0].focus();
						break;
					case 'End':
						e.preventDefault();
						this._triggers[this._triggers.length - 1].focus();
						break;
					case 'Enter':
					case ' ':
						e.preventDefault();
						trigger.click();
						break;
				}
			});

			if (this.settings.openOnHover && !this._isTouchDevice) {
				// Attach hover listeners to the trigger's parent item for better hit area
				const triggerItem = trigger.closest('.accordion-header__item');
				if (triggerItem) {
					triggerItem.addEventListener('mouseenter', this._handleHoverEnter);
					triggerItem.addEventListener('mouseleave', this._handleHoverLeave);
				}
			}
		});

		// Attach mouseleave to panels themselves for "dropdown" behavior
		if (this.settings.openOnHover && !this._isTouchDevice) {
			this._panels.forEach(panel => {
				panel.addEventListener('mouseenter', this._handlePanelHoverEnter);
				panel.addEventListener('mouseleave', this._handlePanelHoverLeave);
			});

			// Close open dropdown when user scrolls (prevents dropdown floating over content)
			this._scrollHandler = () => {
				clearTimeout(this._closeHoverTimeout);
				const activeTrigger = this._triggers.find(t => t.classList.contains('active'));
				if (activeTrigger) this._closeItem(activeTrigger);
			};
			window.addEventListener('scroll', this._scrollHandler, { passive: true });
		}
	}

	/**
	 * Removes all event listeners. Useful for destroying the component.
	 * @private
	 */
	_unbindEvents() {
		this._panels.forEach(panel => {
			panel.removeEventListener('beforematch', this._handleBeforeMatch);
			panel.removeEventListener('transitionend', this._handleTransitionEnd);
		});

		this._triggers.forEach(trigger => {
			trigger.removeEventListener('click', this._handleClick);

			if (this.settings.openOnHover && !this._isTouchDevice) {
				const triggerItem = trigger.closest('.accordion-header__item');
				if (triggerItem) {
					triggerItem.removeEventListener('mouseenter', this._handleHoverEnter);
					triggerItem.removeEventListener('mouseleave', this._handleHoverLeave);
				}
			}
		});

		if (this.settings.openOnHover && !this._isTouchDevice) {
			this._panels.forEach(panel => {
				panel.removeEventListener('mouseenter', this._handlePanelHoverEnter);
				panel.removeEventListener('mouseleave', this._handlePanelHoverLeave);
			});

			if (this._scrollHandler) {
				window.removeEventListener('scroll', this._scrollHandler);
				this._scrollHandler = null;
			}
		}
		this._imageObserver.disconnect();

		if (this._resizeHandler) {
			window.removeEventListener('resize', this._resizeHandler);
			this._resizeHandler = null;
		}
		if (this._resizeRaf) {
			cancelAnimationFrame(this._resizeRaf);
			this._resizeRaf = null;
		}
	}

	/**
	 * Handles click events on accordion triggers.
	 * @param {Event} e The click event.
	 * @private
	 */
	_handleClick = (e) => {
		e.preventDefault();
		clearTimeout(this._closeHoverTimeout); // Clear any pending hover close timeout
		const trigger = e.currentTarget;
		const panel = document.querySelector(trigger.dataset.accordion);
		if (!panel) return;

		const isExpanded = panel.classList.contains('expanded');

		if (this.settings.behavior === 'accordion') {
			this._triggers.forEach(otherTrigger => {
				if (otherTrigger !== trigger && otherTrigger.classList.contains('active')) {
					this._closeItem(otherTrigger);
				}
			});
		}

		if (isExpanded) {
			this._closeItem(trigger);
		} else {
			this._openItem(trigger);
		}
	}

	/**
	 * Reveals a panel that the browser force-opened via find-in-page (Ctrl+F) or
	 * fragment navigation on a `hidden="until-found"` panel. Syncs our aria/active
	 * state and picture/parent-class updates to match the browser's own reveal.
	 * @param {Event} e The beforematch event.
	 * @private
	 */
	_handleBeforeMatch = (e) => {
		const panel = e.target;
		const trigger = this._triggers.find(t => t.dataset.accordion === `#${panel.id}`);
		if (!trigger) return;

		if (this.settings.behavior === 'accordion') {
			this._triggers.forEach(otherTrigger => {
				if (otherTrigger !== trigger && otherTrigger.classList.contains('active')) {
					// Instant: the browser calls its own scrollIntoView() synchronously right
					// after this handler returns — any panel still animating open/closed at
					// that point makes it scroll to a layout that's about to shift, landing in
					// the wrong place (or cropped under the sticky header).
					this._closeItem(otherTrigger, { instant: true });
				}
			});
		}

		this._openItem(trigger, { instant: true });
	}

	/**
	 * Once a panel's open transition finishes, releases its height cap entirely so
	 * later content/viewport changes are never clipped by a stale (or guessed) max-height.
	 * @param {Event} e The transitionend event.
	 * @private
	 */
	_handleTransitionEnd = (e) => {
		const panel = e.currentTarget;
		if (e.propertyName !== 'max-height') return;
		if (panel.classList.contains('expanded')) {
			panel.style.maxHeight = 'none';
		} else {
			// Closing transition finished — now safe to apply hidden="until-found"
			// without cutting the collapse animation short (see _closeItem).
			panel.setAttribute('hidden', 'until-found');
		}
	}

	/**
	 * Handles mouseenter events on accordion triggers for hover functionality.
	 * @param {Event} e The mouseenter event.
	 * @private
	 */
	_handleHoverEnter = (e) => {
		clearTimeout(this._closeHoverTimeout); // Clear any pending close on enter
		const triggerItem = e.currentTarget; // This is the .accordion-header__item
		const trigger = triggerItem.querySelector('[data-accordion]');

		if (!trigger) return;

		const panel = document.querySelector(trigger.dataset.accordion);
		if (!panel) return;

		// If in 'accordion' mode, close any other currently open item.
		if (this.settings.behavior === 'accordion') {
			this._triggers.forEach(otherTrigger => {
				if (otherTrigger !== trigger && otherTrigger.classList.contains('active')) {
					this._closeItem(otherTrigger);
				}
			});
		}

		// Only open if it's not already expanded to prevent re-triggering animation
		if (!panel.classList.contains('expanded')) {
			this._openItem(trigger);
		} else {
			// If already expanded, ensure visual states are correct (e.g., parent class, picture)
			this._updateParentClass(trigger);
			this._updatePicture(trigger);
		}
	}

	/**
	 * Handles mouseleave events from the trigger item, initiating a close timeout.
	 * The item will only close if the cursor does not enter the corresponding panel within the delay.
	 * @param {Event} e The mouseleave event.
	 * @private
	 */
	_handleHoverLeave = (e) => {
		clearTimeout(this._closeHoverTimeout);
		const triggerItem = e.currentTarget; // This is the .accordion-header__item
		const trigger = triggerItem.querySelector('[data-accordion]');
		const panel = document.querySelector(trigger.dataset.accordion);

		if (!panel || !trigger) return;

		// Schedule closing unless the mouse enters the panel quickly
		this._closeHoverTimeout = setTimeout(() => {
			// Check if the panel is currently active before closing
			if (panel.classList.contains('expanded')) {
				this._closeItem(trigger);
			}
		}, this.settings.hoverDelay);
	}

	/**
	 * Handles mouseenter events on an accordion panel. Clears any pending close timeout.
	 * @param {Event} e The mouseenter event.
	 * @private
	 */
	_handlePanelHoverEnter = (e) => {
		clearTimeout(this._closeHoverTimeout);
		// Optional: ensure the associated trigger is active if not already (edge case)
		const panel = e.currentTarget;
		const trigger = this._triggers.find(t => t.dataset.accordion === `#${panel.id}`);
		if (trigger && !trigger.classList.contains('active')) {
			this._openItem(trigger); // Ensure it's open and active if mouse enters panel directly
		}
	}

	/**
	 * Handles mouseleave events from an accordion panel, initiating a close timeout.
	 * This will only close if the mouse does not re-enter the trigger item within the delay.
	 * @param {Event} e The mouseleave event.
	 * @private
	 */
	_handlePanelHoverLeave = (e) => {
		clearTimeout(this._closeHoverTimeout);
		const panel = e.currentTarget;
		const trigger = this._triggers.find(t => t.dataset.accordion === `#${panel.id}`);

		if (!panel || !trigger) return;

		// Schedule closing unless the mouse enters the trigger item quickly
		this._closeHoverTimeout = setTimeout(() => {
			// Check if the panel is currently active before closing
			if (panel.classList.contains('expanded')) {
				this._closeItem(trigger);
			}
		}, this.settings.hoverDelay);
	}

	/**
	 * Opens a specific accordion item.
	 * @param {HTMLElement} trigger The button element that controls the item.
	 * @param {Object} [options]
	 * @param {boolean} [options.instant] Skip the height animation and jump straight to
	 *   the fully-open state. Used for the beforematch/Find-in-Page reveal path, where the
	 *   browser calls its own scrollIntoView() synchronously right after the handler
	 *   returns — an animation still in flight at that point means the target is scrolled
	 *   to based on a layout that's about to change underneath it.
	 * @private
	 */
	_openItem(trigger, { instant = false } = {}) {
		const panelId = trigger.dataset.accordion;
		const panel = document.querySelector(panelId);
		if (!panel || panel.classList.contains('expanded')) return;

		// --- Accessibility updates ---
		trigger.setAttribute('aria-expanded', 'true');
		trigger.setAttribute('tabindex', '0');
		panel.removeAttribute('hidden');
		panel.setAttribute('aria-hidden', 'false');

		this._toggleFocusable(panel, true);

		panel.classList.add('expanded');
		trigger.classList.add('active');

		// Observe the panel for images to lazy load
		if (this._imageObserver && panel) {
			this._imageObserver.observe(panel);
		}

		if (instant) {
			panel.style.maxHeight = 'none';
		} else {
			requestAnimationFrame(() => {
				if (panel.classList.contains('expanded')) {
					panel.style.maxHeight = panel.scrollHeight + 'px';
				}
			});
		}

		this._updateParentClass(trigger);
		this._updatePicture(trigger);
	}

	/**
	 * Closes a specific accordion item.
	 * @param {HTMLElement} trigger The button element that controls the item.
	 * @param {Object} [options]
	 * @param {boolean} [options.instant] Skip the collapse animation and jump straight to
	 *   the fully-closed state — see _openItem's `instant` doc for why.
	 * @private
	 */
	_closeItem(trigger, { instant = false } = {}) {
		const panelId = trigger.dataset.accordion;
		const panel = document.querySelector(panelId);
		if (!panel || !panel.classList.contains('expanded')) return;

		if (instant) {
			panel.classList.remove('expanded');
			panel.style.maxHeight = '0px';
		} else {
			const collapse = () => {
				panel.classList.remove('expanded');
				panel.style.maxHeight = '0px';
			};
			// A fully-open panel has max-height released to "none" (see _handleTransitionEnd)
			// — CSS can't transition away from "none", so restore the exact pixel height first
			// (while .expanded is still applied). A single rAF after that still lands in the
			// same unpainted frame as this synchronous write, so a second rAF is needed to
			// force a real paint of the restored height first — otherwise the collapse to 0
			// is an unanimatable jump instead of a transition.
			if (panel.style.maxHeight === 'none') {
				panel.style.maxHeight = panel.scrollHeight + 'px';
				requestAnimationFrame(() => requestAnimationFrame(collapse));
			} else {
				requestAnimationFrame(collapse);
			}
		}
		trigger.classList.remove('active');

		// --- Accessibility updates ---
		trigger.setAttribute('aria-expanded', 'false');
		// Keep trigger at tabindex=0 so all nav items remain reachable via Tab.
		// Roving-tabIndex on triggers is wrong for a hover dropdown nav: it makes
		// sibling triggers unreachable after the first hover interaction.
		trigger.setAttribute('tabindex', '0');
		panel.setAttribute('aria-hidden', 'true');
		// Setting hidden="until-found" immediately would flip content-visibility to hidden
		// right away, collapsing the panel's children instantly — unlike the FAQ accordion,
		// this panel's own box has no padding of its own to keep animating (its padding
		// lives on a nested __content element), so there'd be nothing left to visibly
		// transition. Deferred to _handleTransitionEnd so the collapse plays first, except
		// under reduced motion or an instant close, where no transition plays and it must
		// be set right away.
		if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			panel.setAttribute('hidden', 'until-found');
		}

		this._toggleFocusable(panel, false);

		// Stop observing the panel for images once it's closed
		if (this._imageObserver && panel) {
			this._imageObserver.unobserve(panel);
		}

		// Update classes and picture only if no other item is active
		const activeTriggerCount = this._triggers.filter(t => t.classList.contains('active')).length;
		if (activeTriggerCount === 0) {
			this._updateParentClass(null);
			this._updatePicture(null);
		}
	}


	_focusNext(index) {
		const next = (index + 1) % this._triggers.length;
		this._triggers[next].focus();
	}

	_focusPrev(index) {
		const prev = (index - 1 + this._triggers.length) % this._triggers.length;
		this._triggers[prev].focus();
	}

	/**
	 * Updates the main parent container's class to reflect the active tab.
	 * @param {HTMLElement|null} activeTrigger The trigger that is now active, or null if none are.
	 * @private
	 */
	_updateParentClass(activeTrigger) {
		if (!this._mainParent) return;

		this._mainParent.className = this._mainParent.className.replace(/\bactive-tab-\S+/g, '').trim();
		if (activeTrigger) {
			const panelId = activeTrigger.dataset.accordion.replace('#', '');
			this._mainParent.classList.add(`active-tab-${panelId}`);
		}
	}

	/**
	 * Updates the picture with a smooth fade transition.
	 * @param {HTMLElement|null} activeTrigger The trigger that is now active.
	 * @private
	 */
	_updatePicture(activeTrigger) {
		if (!this._pictureContainer || !this._imageTarget) return;

		clearTimeout(this._pictureUpdateTimeout);

		const newImagePath = activeTrigger ? activeTrigger.dataset.imagePath : null;

		// 1. Start fade-out by adding the 'is-changing' class.
		this._pictureContainer.classList.add('is-changing');

		// 2. Wait for the fade-out transition to finish before swapping source.
		this._pictureUpdateTimeout = setTimeout(() => {
			// 3. Once invisible, swap the image source and update classes.
			if (newImagePath) {
				this._imageTarget.src = newImagePath;
			}

			// Update classes on the picture container.
			this._pictureContainer.className = this._pictureContainer.className.replace(/\bactive-tab-\S+/g, '').trim();
			if (activeTrigger) {
				const panelId = activeTrigger.dataset.accordion.replace('#', '');
				this._pictureContainer.classList.add(`active-tab-${panelId}`);
			}

			// 4. Start fade-in by removing the 'is-changing' class.
			this._pictureContainer.classList.remove('is-changing');

			// 5. Update heights dynamically (new image might change height)
			this.updateHeights();
		}, this.settings.transitionDuration);
	}

	/**
	 * Handles IntersectionObserver entries for lazy loading images.
	 * @param {Array<IntersectionObserverEntry>} entries The array of entries.
	 * @private
	 */
	_handleImageIntersection(entries, observer) {
		entries.forEach(entry => {
			// Only load images if the panel is intersecting AND currently expanded
			if (entry.isIntersecting && entry.target.classList.contains('expanded')) {
				const panel = entry.target;
				const imagesToLoad = panel.querySelectorAll('img[data-src]');
				imagesToLoad.forEach(img => {
					img.src = img.dataset.src;
					img.removeAttribute('data-src');
				});
				observer.unobserve(panel);
			}
		});
	}


	/**
	 * Enables or disables focusable elements inside a panel for accessibility.
	 * Prevents links/buttons inside aria-hidden panels from being focusable.
	 * @param {HTMLElement} panel - The panel element.
	 * @param {boolean} enable - Whether to enable or disable focusability.
	 * @private
	 */
	_toggleFocusable(panel, enable) {
		if (!panel) return;
		const focusables = panel.querySelectorAll('a, button, input, textarea, select, [tabindex]');
		focusables.forEach(el => {
			if (enable) {
				if (el.dataset._prevTabindex !== undefined) {
					el.setAttribute('tabindex', el.dataset._prevTabindex);
					delete el.dataset._prevTabindex;
				} else {
					el.removeAttribute('tabindex');
				}
			} else {
				// store current tabindex if any
				if (el.hasAttribute('tabindex')) {
					el.dataset._prevTabindex = el.getAttribute('tabindex');
				}
				el.setAttribute('tabindex', '-1');
			}
		});
	}


	/**
	 * Update height of all expanded panels dynamically.
	 * Called on resize and can be called externally.
	 */
	updateHeights() {
		this._panels.forEach(panel => {
			if (panel.classList.contains('expanded')) {
				panel.style.maxHeight = panel.scrollHeight + 'px';
			}
		});
	}

	_initialize() {
		// Ensure all initially-closed panels have their inner focusables disabled.
		// _toggleFocusable is only called inside _openItem/_closeItem, so panels that
		// are never explicitly opened on load would otherwise expose hidden links to Tab.
		this._panels.forEach(panel => {
			if (!panel.classList.contains('expanded')) {
				this._toggleFocusable(panel, false);
			}
		});

		const initialTrigger = this.container.querySelector('[data-accordion-initial="true"]');
		if (initialTrigger) {
			// Delay opening slightly to allow CSS transitions to initialize
			requestAnimationFrame(() => {
				// Double RAF ensures browser paints initial hidden state before transition
				requestAnimationFrame(() => this._openItem(initialTrigger));
			});
		}
	}

	/**
	 * Destroys the accordion instance, removing all event listeners and clearing timeouts.
	 */
	destroy() {
		this._unbindEvents();
		clearTimeout(this._pictureUpdateTimeout);
		clearTimeout(this._closeHoverTimeout);
		// console.log('Accordion instance destroyed:', this.container);
	}
}

/**
 * Initializes a new Accordion instance.
 * Keeps a reference for external access (used by setupAccordionPictureRelocator).
 */
export function initializeAccordion(container) {
	if (container.dataset.accordionInstance) {
		// console.warn('Accordion already initialized for this container.', container);
		return;
	}
	const instance = new Accordion(container);
	container.dataset.accordionInstance = 'true';
	container.__accordionInstance = instance; // Save reference globally
	return instance;
}
